/**
 * Custom Tone Presets Hook (F-09)
 *
 * Provides CRUD operations for user-defined tone/instruction presets,
 * persisted to localStorage for cross-session reuse.
 */

import { useState, useCallback, useEffect } from 'react';

// ── Types ──────────────────────────────────────────────────────────────

export interface CustomPreset {
    id: string;
    name: string;
    prompt: string;
    createdAt: number;
}

// ── Constants ──────────────────────────────────────────────────────────

const STORAGE_KEY = 'ai-edit-custom-presets';
const MAX_PRESETS = 20;

// ── Hook ───────────────────────────────────────────────────────────────

export const useCustomPresets = () => {
    const [presets, setPresets] = useState<CustomPreset[]>(() => loadPresets());

    // Sync state when another tab modifies localStorage
    useEffect(() => {
        const handleStorage = (e: StorageEvent) => {
            if (e.key === STORAGE_KEY) {
                setPresets(loadPresets());
            }
        };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);

    const persist = useCallback((updated: CustomPreset[]): boolean => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
            setPresets(updated);
            return true;
        } catch (err) {
            if (err instanceof DOMException && err.name === 'QuotaExceededError') {
                console.warn('[CustomPresets] localStorage quota exceeded — preset could not be saved.');
            } else {
                console.warn('[CustomPresets] Failed to save presets:', err);
            }
            return false;
        }
    }, []);

    const addPreset = useCallback((name: string, prompt: string): CustomPreset | null => {
        if (!name.trim() || !prompt.trim()) return null;

        const current = loadPresets();
        if (current.length >= MAX_PRESETS) {
            console.warn(`[CustomPresets] Preset limit (${MAX_PRESETS}) reached. Delete an existing preset before adding a new one.`);
            return null;
        }

        const preset: CustomPreset = {
            id: `preset_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
            name: name.trim(),
            prompt: prompt.trim(),
            createdAt: Date.now(),
        };

        const updated = [preset, ...current];
        const saved = persist(updated);
        return saved ? preset : null;
    }, [persist]);

    const deletePreset = useCallback((id: string) => {
        const current = loadPresets();
        const updated = current.filter(p => p.id !== id);
        persist(updated);
    }, [persist]);

    const updatePreset = useCallback((id: string, data: Partial<Pick<CustomPreset, 'name' | 'prompt'>>) => {
        const current = loadPresets();
        const updated = current.map(p => {
            if (p.id !== id) return p;
            return {
                ...p,
                ...(data.name !== undefined ? { name: data.name.trim() } : {}),
                ...(data.prompt !== undefined ? { prompt: data.prompt.trim() } : {}),
            };
        });
        persist(updated);
    }, [persist]);

    const isAtLimit = useCallback((): boolean => {
        return loadPresets().length >= MAX_PRESETS;
    }, []);

    return {
        presets,
        addPreset,
        deletePreset,
        updatePreset,
        isAtLimit,
        maxPresets: MAX_PRESETS,
    };
};

// ── Helpers ────────────────────────────────────────────────────────────

function loadPresets(): CustomPreset[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}
